import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import qs from 'query-string';
import { types } from 'starknet';

import { Address } from '@ton/core';
import { BASE_URL, BULK_API_URL } from '../Config/ApiConfig';
import {
  amountOptionsForLimits,
  maxPlatformDecimals,
} from '../constants/numbers';
import { NetworkTypes } from '../providers/web3Provider';
import { AppTypedData } from '@/starknet/types/app_typed_data';
import { appStore } from '../stores/app.store';
import { networksStore } from '../stores/networks.store';
import { useSignStore } from '../stores/sign.store';
import { walletStore } from '../stores/wallet.store';
import {
  GetCurrencyPricesResponse,
  IAmountOut,
  IApiResp,
  ICurrency,
  ICurrencyWithPairs,
  IGetNetwork,
  ILimits,
  INetwork,
  IOrder,
  IOrderInfo,
  IOrderResponse,
  IReceiver,
  ISimpleApiResp,
  ITransactionFee,
  UnsupportedContracts,
} from '../types/apiTypes';
import { OrderSearchStatuses } from '../types/enums';
import { isEth, logInGroup } from '../utils';
import { getCookie } from '../utils/cookies';
import { formatAmountBySymbol, toFixed } from '../utils/numbers';
import { ApiService as Api } from './CoreApi';
import { mockTron } from '@/constants/tron';
import { retroPointsService } from './retroPoints';

export const isDevelopment = process.env.NODE_ENV === 'development';
export const networkTypeName = 'network-type';
export const apiKey = 'api-key';

const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  const api_key = getCookie('api_key');
  if (api_key) {
    config.headers[apiKey] = api_key;
  }
  return config;
};

export const networkTypeSelector = (network: INetwork) => {
  const type =
    network.network_type === NetworkTypes.ZK_SYNC_ERA
      ? NetworkTypes.EVM
      : network.network_type;

  return type;
};

export class ApiService {
  private _api: Api;
  constructor() {
    this._api = new Api(BASE_URL, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });

    this._api.instance.defaults.headers.put['Content-Type'] =
      'application/json';
    this._api.instance.interceptors.request.use(config =>
      requestInterceptor(config)
    );
  }

  async getNetwork() {
    const { setNetworks, setIsLoading } = networksStore.getState();
    const url = '/api/network';
    try {
      setIsLoading(true);

      const { data }: IGetNetwork = await this._api.get(url);

      if (!data.some(network => network.chainId === mockTron.chainId)) {
        data.push(mockTron);
      }

      setNetworks(data);

      return data;
    } catch (error) {
      isDevelopment && console.log(error, '======error getNetworks');
    } finally {
      setIsLoading(false);
    }
    return undefined;
  }

  async getCurrencyPairs(networkFromId: string, networkToId: string) {
    const { setSourceCurrencies, setIsLoading } = appStore.getState();

    if (!networkFromId || !networkToId) {
      return;
    }
    const url = `/api/currency/${networkFromId}/${networkToId}/pairs`;

    try {
      setIsLoading(true);

      const res: IApiResp<ICurrencyWithPairs[]> = await this._api.get(url);
      const sortedData = res.data.sort((a, b) => {
        if (isEth(a.symbol)) return -1;
        if (isEth(b.symbol)) return 1;
        return b.symbol.localeCompare(a.symbol);
      });
      setSourceCurrencies(sortedData);
    } catch (error) {
      isDevelopment && console.log(error, '======error getCurrencyPairs');
    } finally {
      setIsLoading(false);
    }
  }

  async getBridgePairsForCurrency(
    currencyId: string,
    callback?: (currencyPairs: ICurrency[]) => void
  ) {
    const { setAllCurrencyPairs } = walletStore.getState();

    const url = `/api/currency/${currencyId}/pairs?excludeCrossChain=true&limit=100`;

    try {
      const res: ISimpleApiResp<IApiResp<ICurrency[]>> = await this._api.get(
        `${url}`
      );

      if (res.data) {
        const sortedData = res.data.data.sort((a, b) => {
          if (isEth(a.symbol)) return -1;
          if (isEth(b.symbol)) return 1;
          return b.symbol.localeCompare(a.symbol);
        });
        if (!callback) setAllCurrencyPairs(sortedData);
        else callback(sortedData);
      }
    } catch (error) {
      isDevelopment &&
        console.log(error, '======error getBridgePairsForCurrency');
    }
    return undefined;
  }

  async getLimits(currencyFrom: ICurrency, currencyTo: ICurrency) {
    const { setMinSend, setMaxSend } = appStore.getState();
    const url = `/api/orders/limits/${currencyFrom.id}/${currencyTo.id}`;
    try {
      const res: ISimpleApiResp<ILimits> = await this._api.get(url);

      if (res.data) {
        setMinSend(
          +formatAmountBySymbol(
            res.data.min_send,
            currencyFrom.symbol,
            amountOptionsForLimits
          )
        );
        setMaxSend(
          +formatAmountBySymbol(
            res.data.max_send,
            currencyFrom.symbol,
            amountOptionsForLimits
          )
        );
      } else {
        setMinSend(
          +formatAmountBySymbol(
            currencyFrom.min_send,
            currencyFrom.symbol,
            amountOptionsForLimits
          )
        );
        setMaxSend(
          +formatAmountBySymbol(
            currencyFrom.max_send,
            currencyFrom.symbol,
            amountOptionsForLimits
          )
        );
      }
    } catch (error) {
      setMinSend(
        +formatAmountBySymbol(
          currencyFrom.min_send,
          currencyFrom.symbol,
          amountOptionsForLimits
        )
      );
      setMaxSend(
        +formatAmountBySymbol(
          currencyFrom.max_send,
          currencyFrom.symbol,
          amountOptionsForLimits
        )
      );

      isDevelopment && console.log(error, '======error getLimits');
      if (error instanceof AxiosError) {
        throw new Error(`${error.response?.data.message}`);
      }
    }
  }

  async getAmountOut(
    currencyFromId: string,
    currencyToId: string,
    amount: string,
    walletFrom?: string,
    walletTo?: string
  ) {
    const {
      setIsLoading,
      setAmountTo,
      setTransactionFee,
      setError,
      amountOutAbortController,
      setAmountOutAbortController,
    } = appStore.getState();
    const url = `/api/orders/calculate_amount_out/${currencyFromId}/${currencyToId}`;
    const params = qs.stringify({
      amount,
      wallet_sender: walletFrom,
      wallet_receiver: walletTo,
    });
    try {
      setIsLoading(true);

      if (amountOutAbortController) amountOutAbortController.abort();
      setAmountOutAbortController(new AbortController());

      const res: ISimpleApiResp<IAmountOut & ITransactionFee> =
        await this._api.get(`${url}/?${params}`);

      if (res.data) {
        setAmountTo(toFixed(res.data.amount_out, maxPlatformDecimals));
        setTransactionFee(res.data);
      } else {
        setAmountTo('');
        setTransactionFee(null);
      }
      setAmountOutAbortController(null);
    } catch (error) {
      const err = error as AxiosError;
      if (err.name === 'AbortError') return;

      setAmountTo('');
      isDevelopment && console.log(error, '======error getAmountOut');
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          if (
            error.response.data.message.startsWith(
              'Too small amount to send'
            ) ||
            error.response.data.message.startsWith(
              `The amount you're trying to send must be greater`
            )
          )
            setError(
              'Too small amount to send. It will not cover destination fees'
            );
        } else
          setError(
            error.response?.status === 400
              ? error.response.data.message
              : 'Temporary transfer limit'
          );

        throw new Error(`${error.response?.data.message}`);
      } else {
        setError('Temporary transfer limit');
      }
    } finally {
      setIsLoading(false);
    }
  }

  async getReceiver(currencyFrom: ICurrency, currencyTo: ICurrency) {
    const { setIsLoading, setReceiver } = appStore.getState();
    const url = `/api/orders/receiver?currency_in_id=${currencyFrom?.id}&currency_out_id=${currencyTo?.id}`;
    try {
      setIsLoading(true);

      const res: IApiResp<IReceiver> = await this._api.get(url);

      if (res.data) {
        setReceiver(res.data.wallet);
      }
    } catch (error) {
      isDevelopment && console.log(error, '======error getReceiveAmount');
    } finally {
      setIsLoading(false);
    }
  }

  async getOrderById(orderId: string, setAsCurrent = true) {
    const { setCurrentOrder } = walletStore.getState();
    const url = `${BULK_API_URL}/api/bulk_order/info/${orderId}`;
    try {
      const res: { message: string; data: IOrderInfo } =
        await this._api.get(url);
      if (res && setAsCurrent) {
        setCurrentOrder(res.data);
      }
      return res.data;
    } catch (error) {
      isDevelopment && console.log(error, '======error getOrderById');
    }
    return undefined;
  }

  async sendOrder(
    fromCurrency: ICurrency,
    toCurrency: ICurrency,
    amount: string,
    wallet_sender: string,
    wallet_receiver: string
  ) {
    const { setIsLoading } = appStore.getState();
    const { setCurrentOrder } = walletStore.getState();
    const url = '/api/orders';
    const type = networkTypeSelector(fromCurrency.contract.network);
    try {
      setIsLoading(true);
      const data = {
        currency_in_id: fromCurrency.id,
        currency_out_id: toCurrency.id,
        amount,
        wallet_sender:
          type === NetworkTypes.TON
            ? Address.parse(wallet_sender).toRawString()
            : wallet_sender,
        wallet_receiver,
      };

      const res: { message: string; data: IOrder } = await this._api.post(
        url,
        JSON.stringify(data),
        {
          headers: {
            [networkTypeName]: type,
          },
        }
      );

      if (res) {
        setCurrentOrder(res.data);
      }

      return res.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 401) {
        const { setResign } = useSignStore.getState();
        setResign(type);
      }
      isDevelopment && console.log(error, '======error sendOrder');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  async getOrders(
    status: OrderSearchStatuses,
    search?: string,
    wallets?: (string | undefined)[],
    setToStore = true,
    skip = 0,
    take = 50,
    includeBulkOrders = false
  ) {
    const { setOrders, setIsLoading } = walletStore.getState();
    const url = '/api/orders';

    try {
      if (setToStore) setIsLoading(true);

      let data: IOrder[] = [];

      if ((wallets?.length ?? 0) <= 0) return [];

      if (take <= 50) {
        const res: IApiResp<IOrderResponse> = await this._api.get(url, {
          params: {
            take,
            skip,
            status,
            include_bulk_orders: includeBulkOrders,
            search: search || undefined,
            wallets: JSON.stringify(wallets) || undefined,
          },
        });
        data = res.data.data;
      } else {
        for (let i = 0; i * 50 < take; i++) {
          const res: IApiResp<IOrderResponse> = await this._api.get(url, {
            params: {
              take: 50,
              skip: skip + i * 50,
              status,
              include_bulk_orders: includeBulkOrders,
              search: search || undefined,
              wallet: wallets || undefined,
            },
          });

          data = [...data, ...res.data.data];
        }
      }

      if (data && setToStore) {
        setOrders(data);
      }

      if (setToStore) setIsLoading(false);
      return data;
    } catch (error) {
      isDevelopment && console.log(error, '======error getOrders');
      setIsLoading(false);
    }
    return undefined;
  }

  async getSignMessage() {
    const url = '/api/wallet_auth/message';
    try {
      const res: ISimpleApiResp<AppTypedData> = await this._api.get(url);

      return res;
    } catch (error) {
      isDevelopment && console.log(error, '======error getSignMessage');
      throw error;
    }
  }

  async validateSignedMessage(
    wallet_address: string,
    network_type: NetworkTypes,
    signature: string | types.Signature
  ) {
    const url = '/api/wallet_auth/message';
    try {
      await this._api.post<
        ISimpleApiResp<{
          network_type: NetworkTypes;
          token: string;
        }>
      >(url, { wallet_address, network_type, signature });

      if (window.location.href.includes('/quests')) {
        await retroPointsService.bindWallet(
          undefined,
          wallet_address,
          network_type
        );
      } else if (network_type === NetworkTypes.EVM) {
        await retroPointsService.bindWallet(
          undefined,
          wallet_address,
          network_type
        );
      }
    } catch (error) {
      isDevelopment && console.log(error, '======error validateSignedMessage');
      throw error;
    }
  }

  async checkWallet(wallet_address: string, network_type: NetworkTypes) {
    const url = `/api/wallet_auth/wallet/${wallet_address}`;
    return this._api.get(url, {
      headers: { [networkTypeName]: network_type },
    });
  }

  async sendApiKeyRequest(data: {
    name: string;
    email: string;
    telegramUsername: string;
    description: string;
  }) {
    const url = '/api/dashboard/request-api-key';
    return await this._api.post(url, data);
  }

  async getPrices(symbol?: string | string[]) {
    const url = '/api/prices';

    const response: GetCurrencyPricesResponse = await this._api.get(url, {
      params: symbol,
    });

    return response.data;
  }

  async closeOrder(orderId: string, network_type: INetwork) {
    const url = `api/orders/close/${orderId}`;

    return await this._api.put(
      url,
      {},
      {
        headers: {
          [networkTypeName]: networkTypeSelector(network_type),
        },
      }
    );
  }

  async setOrderAsFailed(orderId: string, network_type: INetwork) {
    const url = `api/orders/set_order_as_failed/${orderId}`;

    return await this._api.put(
      url,
      {},
      {
        headers: {
          [networkTypeName]: networkTypeSelector(network_type),
        },
      }
    );
  }

  async getAmountOfOrders() {
    try {
      const url = '/api/orders/get_total_count_of_orders';
      const { data } = await this._api.get<{ data: { total_count: number } }>(
        url
      );

      return data.total_count;
    } catch (error) {
      logInGroup('[API] Error while getting amount of orders', error);
    }
    return 0;
  }

  async getUnsupportedContracts() {
    try {
      const url = '/api/contract/unused_contracts';
      const data = await this._api.get<UnsupportedContracts[]>(url);

      return data;
    } catch (error) {
      logInGroup('[API] Error while getting unsupported contracts', error);
    }
    return [];
  }
}

export const api = new ApiService();
